/**
 * Display formatting. One function per intent, so a rand amount never renders
 * two different ways on two screens.
 *
 * Money in this app is always South African rand. There is no multi-currency
 * story and there is not going to be one, so the currency code is a constant
 * rather than an argument that every caller has to remember to pass.
 */

/**
 * R51 000.00 — the form used anywhere a number is part of a total that must add
 * up: quotes, invoices, statements, the pricing breakdown.
 *
 * Built by hand rather than through `Intl.NumberFormat('en-ZA', …)`, which
 * produces 'R 51 000,00' — a space after the R and a comma before the cents.
 * That is the SI convention South African government forms use, and it is not
 * what a wedding client has ever seen on an invoice. Commercial practice here,
 * and what Sage, Xero and every studio in the country prints, is 'R51 000.00':
 * no space after the symbol, a space between thousands, a full stop before the
 * cents. This string ends up on a document a client pays against, so it follows
 * the document convention rather than the standards body.
 */
export function formatMoney(amount: number): string {
  return signed(amount, groupThousands(amount, 2))
}

/** R51 000 — headline figures where cents are noise. Never used on a document. */
export function formatMoneyWhole(amount: number): string {
  return signed(amount, groupThousands(amount, 0))
}

/**
 * The minus goes BEFORE the currency symbol: -R792.66, not R-792.66. The
 * second reads as a currency called "R-" and is what you get from naive
 * concatenation; a discount line on a quote has to look like money.
 */
function signed(amount: number, magnitude: string): string {
  return amount < 0 ? `-R${magnitude}` : `R${magnitude}`
}

/**
 * The absolute value, to fixed decimals, with thousands separated by a
 * NON-BREAKING space so a total never wraps mid-number in a narrow table cell.
 * The sign is applied by `signed`.
 */
function groupThousands(amount: number, decimals: number): string {
  const fixed = Math.abs(amount).toFixed(decimals)
  const [whole = '0', fraction] = fixed.split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')

  return fraction ? `${grouped}.${fraction}` : grouped
}

/**
 * Money as typed into a form. Returns null for anything that is not a number,
 * so a form can tell 'empty' from 'zero' — which matters, because a zero
 * discount and no discount print differently.
 */
export function parseMoney(input: string): number | null {
  const cleaned = input.replace(/[R\s ,]/g, '').trim()
  if (cleaned === '') return null

  const value = Number(cleaned)
  return Number.isFinite(value) ? value : null
}

/** 15% — takes the fraction as stored (0.15), not the percentage. */
export function formatPercent(fraction: number, decimals = 0): string {
  return `${(fraction * 100).toFixed(decimals).replace(/\.0+$/, '')}%`
}

/** 412 km. Distances are whole kilometres; nobody quotes travel to the metre. */
export function formatDistance(km: number | null | undefined): string {
  if (km === null || km === undefined) return '—'
  return `${Math.round(km)} km`
}

/** 4 h 35 min — a driving time, from a duration in minutes. */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—'

  const hours = Math.floor(minutes / 60)
  const rest = Math.round(minutes % 60)

  if (hours === 0) return `${rest} min`
  if (rest === 0) return `${hours} h`
  return `${hours} h ${rest} min`
}

/**
 * The couple, as one string: 'Amy & Mark'. Falls back gracefully while an
 * enquiry still has only one name attached.
 */
export function formatCouple(
  first: { first_name: string; last_name?: string | null } | null | undefined,
  second?: { first_name: string; last_name?: string | null } | null,
): string {
  // Trim before testing, not after. A first_name of '  ' is truthy, so filtering
  // on the raw value lets a whitespace-only name through and the join then
  // renders an empty string where a couple's names should be.
  const names = [first, second].map((person) => person?.first_name?.trim() ?? '').filter((name) => name.length > 0)

  if (names.length === 0) return 'Unnamed'
  return names.join(' & ')
}

export function formatFullName(person: {
  first_name: string
  last_name?: string | null
}): string {
  return [person.first_name, person.last_name].filter(Boolean).join(' ').trim()
}

/**
 * South African numbers, spaced the way they are read aloud: 082 123 4567.
 * Anything that is not recognisably a local number is returned untouched rather
 * than mangled — an international client's number should survive a round trip.
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '—'

  const digits = phone.replace(/[^\d+]/g, '')

  if (/^0\d{9}$/.test(digits)) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`
  }

  if (/^\+27\d{9}$/.test(digits)) {
    return `+27 ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`
  }

  return phone
}

/** Turns 'Somerset West, Cape Town, Western Cape, South Africa' into 'Somerset West'. */
export function townFrom(address: string | null | undefined): string {
  if (!address) return '—'
  const [town] = address.split(',')
  return town?.trim() || address
}

/** Two initials for the avatar. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase() || '?'
}

/** '3 quotes' / '1 quote' — small, but it is everywhere. */
export function pluralise(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`
}
